import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, errMsg } from '../lib/api';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { useI18n } from '../lib/i18n';
import { ClipboardText } from '@phosphor-icons/react';

const Card = ({ children, testid }) => <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid={testid}>{children}</div>;
const STATUS_BADGE = { registered: 'bg-green-100 text-green-700', waitlisted: 'bg-amber-100 text-amber-700', offered: 'bg-blue-100 text-blue-700', dropped: 'bg-slate-100 text-slate-400', withdrawn: 'bg-rose-100 text-rose-600' };
const HOLD_TYPES = ['financial', 'academic', 'disciplinary', 'documentary', 'medical', 'library'];
const HOLD_BLOCKS = ['registration', 'transcript', 'results', 'graduation'];

const RuleRow = ({ r, onOverride, overrideValue }) => (
  <div className={`flex items-start gap-2 py-1 border-t border-slate-50 text-xs ${r.passed ? 'text-slate-500' : r.overridden ? 'text-blue-700' : 'text-rose-600'}`}>
    <span className="w-4">{r.passed ? '✓' : r.overridden ? '⤳' : '✕'}</span>
    <span className="font-medium w-44 shrink-0">{r.name}</span>
    <span className="flex-1">{r.message}{!r.passed && r.action ? <span className="block text-slate-400">{r.action}</span> : null}</span>
    {!r.passed && onOverride && (
      <Input data-testid={`override-${r.rule}`} placeholder="Override reason" className="h-7 text-xs w-48"
        value={overrideValue || ''} onChange={(e) => onOverride(r.rule, e.target.value)} />
    )}
  </div>
);

const StudentPicker = ({ students, value, onChange, testid }) => {
  const [q, setQ] = useState('');
  const list = (students || []).filter((s) => !q || s.name.toLowerCase().includes(q.toLowerCase()) || (s.admission_no || '').toLowerCase().includes(q.toLowerCase()));
  const sel = (students || []).find((s) => s.id === value);
  return (
    <div>
      <Input data-testid={testid} placeholder="Search by name or admission number" value={sel && !q ? sel.name : q}
        onChange={(e) => { setQ(e.target.value); onChange(null); }} />
      {q && !sel && (
        <div className="border border-slate-200 rounded-md mt-1 max-h-40 overflow-auto bg-white shadow-sm">
          {list.slice(0, 8).map((s) => (
            <button key={s.id} data-testid={`pick-student-${s.id}`} className="block w-full text-left px-3 py-1.5 text-sm hover:bg-slate-50"
              onClick={() => { onChange(s.id); setQ(''); }}>{s.name} <span className="text-xs text-slate-400">Grade {s.grade} · {s.admission_no}</span></button>
          ))}
          {list.length === 0 && <p className="px-3 py-2 text-xs text-slate-400">No matching student</p>}
        </div>
      )}
    </div>
  );
};

const SectionMultiPick = ({ sections, chosen, setChosen }) => (
  <div className="flex flex-wrap gap-1.5">
    {(sections || []).map((s) => {
      const on = chosen.includes(s.id);
      return (
        <button key={s.id} data-testid={`pick-section-${s.id}`}
          className={`text-xs border rounded-full px-2.5 py-1 ${on ? 'bg-slate-900 text-white border-slate-900' : 'border-slate-200 hover:bg-slate-50'}`}
          onClick={() => setChosen(on ? chosen.filter((x) => x !== s.id) : [...chosen, s.id])}>
          {s.course_code} {s.section_code}
        </button>
      );
    })}
  </div>
);

const RegisterTab = ({ students, sections }) => {
  const [studentId, setStudentId] = useState(null);
  const [chosen, setChosen] = useState([]);
  const [overrides, setOverrides] = useState({});
  const [check, setCheck] = useState(null);
  const setOv = (rule, v) => setOverrides((o) => ({ ...o, [rule]: v }));
  const doCheck = async () => {
    try { setCheck((await api.post('/reg/check', { student_id: studentId, section_ids: chosen, overrides })).data); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const doRegister = async () => {
    try {
      const { data } = await api.post('/reg/register', { student_id: studentId, section_ids: chosen, overrides });
      if (!data.ok) { setCheck({ sections: data.failed, ok: false }); toast.error('Some rules still block this registration - review below'); return; }
      toast.success(`Registered: ${data.registered.map((r) => `${r.course_code} (${r.status})`).join(', ')}`);
      setCheck(null); setChosen([]); setOverrides({});
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Card testid="regdesk-register">
      <div className="grid md:grid-cols-2 gap-4">
        <div><p className="text-xs font-medium mb-1">Student</p><StudentPicker students={students} value={studentId} onChange={setStudentId} testid="reg-student-search" /></div>
        <div><p className="text-xs font-medium mb-1">Sections</p><SectionMultiPick sections={sections} chosen={chosen} setChosen={setChosen} /></div>
      </div>
      <div className="flex gap-2 mt-4">
        <Button data-testid="reg-check-btn" variant="outline" disabled={!studentId || chosen.length === 0} onClick={doCheck}>Check eligibility</Button>
        <Button data-testid="reg-register-btn" className="bg-slate-900 hover:bg-slate-800" disabled={!studentId || chosen.length === 0} onClick={doRegister}>Register</Button>
      </div>
      {check && (check.sections || []).map((sec) => (
        <div key={sec.section_id} className="mt-4 border border-slate-200 rounded-lg p-3" data-testid={`check-result-${sec.section_id}`}>
          <p className="text-sm font-semibold mb-1">{sec.course_code} {sec.section_code}
            <span className={`ml-2 text-[10px] px-2 py-0.5 rounded ${sec.ok ? 'bg-green-100 text-green-700' : 'bg-rose-100 text-rose-600'}`}>{sec.ok ? (sec.would_waitlist ? 'eligible - waitlist' : 'eligible') : 'blocked'}</span></p>
          {sec.results.map((r) => <RuleRow key={r.rule} r={r} onOverride={setOv} overrideValue={overrides[r.rule]} />)}
        </div>
      ))}
    </Card>
  );
};

const BulkTab = ({ sections }) => {
  const [grade, setGrade] = useState('');
  const [chosen, setChosen] = useState([]);
  const [preview, setPreview] = useState(null);
  const doPreview = async () => {
    try { setPreview((await api.post('/reg/bulk/preview', { grade, section_ids: chosen })).data); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const doCommit = async () => {
    try {
      const { data } = await api.post('/reg/bulk/commit', { grade, section_ids: chosen });
      toast.success(`Registered ${data.registered}, waitlisted ${data.waitlisted}, skipped ${data.skipped.length}`);
      setPreview(null);
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Card testid="regdesk-bulk">
      <div className="grid md:grid-cols-2 gap-4">
        <div><p className="text-xs font-medium mb-1">Grade / cohort</p>
          <Input data-testid="bulk-grade" placeholder="e.g. 9" value={grade} onChange={(e) => setGrade(e.target.value)} /></div>
        <div><p className="text-xs font-medium mb-1">Section set</p><SectionMultiPick sections={sections} chosen={chosen} setChosen={setChosen} /></div>
      </div>
      <div className="flex gap-2 mt-4">
        <Button data-testid="bulk-preview-btn" variant="outline" disabled={!grade || chosen.length === 0} onClick={doPreview}>Preview - who succeeds and who fails</Button>
        {preview && <Button data-testid="bulk-commit-btn" className="bg-slate-900 hover:bg-slate-800" onClick={doCommit}>Register {preview.fully_eligible} of {preview.total} students</Button>}
      </div>
      {preview && (
        <div className="mt-4 overflow-x-auto" data-testid="bulk-preview-table">
          <table className="w-full text-sm">
            <thead><tr className="text-left border-b border-slate-100"><th className="py-1.5 pr-3 font-mono-label text-[10px] uppercase text-slate-400">Student</th><th className="font-mono-label text-[10px] uppercase text-slate-400">Outcome per section</th></tr></thead>
            <tbody>{preview.rows.map((row) => (
              <tr key={row.student_id} className="border-b border-slate-50 align-top">
                <td className="py-1.5 pr-3 font-medium">{row.name}</td>
                <td className="py-1.5">{row.sections.map((x) => (
                  <span key={x.section_id} className={`inline-block mr-2 mb-1 text-xs px-2 py-0.5 rounded ${x.already ? 'bg-slate-100 text-slate-400' : x.ok ? (x.would_waitlist ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700') : 'bg-rose-100 text-rose-600'}`}
                    title={x.why || ''}>{x.course_code}: {x.already ? 'already on it' : x.ok ? (x.would_waitlist ? 'waitlist' : 'will register') : x.why}</span>
                ))}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
    </Card>
  );
};

const RosterTab = ({ sections, students }) => {
  const qc = useQueryClient();
  const [sectionId, setSectionId] = useState('');
  const [addId, setAddId] = useState(null);
  const { data: roster } = useQuery({ enabled: !!sectionId, queryKey: ['roster', sectionId], queryFn: () => api.get(`/acad/registrations?section_id=${sectionId}`).then((r) => r.data) });
  const refresh = () => qc.invalidateQueries({ queryKey: ['roster', sectionId] });
  const drop = async (rid) => {
    try { const { data } = await api.post('/reg/drop', { registration_id: rid, reason: 'Dropped from the roster' }); toast.success(data.outcome === 'withdrawn' ? `Withdrawn with grade code ${data.grade_code}` : 'Dropped'); refresh(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const add = async () => {
    try {
      const { data } = await api.post('/reg/register', { student_id: addId, section_ids: [sectionId] });
      if (!data.ok) { toast.error(data.failed[0]?.first_failure?.message || 'Registration blocked'); return; }
      toast.success('Added to the section'); setAddId(null); refresh();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Card testid="regdesk-roster">
      <select data-testid="roster-section-select" className="border border-slate-200 rounded-md text-sm px-2 py-2 mb-3" value={sectionId} onChange={(e) => setSectionId(e.target.value)}>
        <option value="">Pick a section</option>
        {(sections || []).map((s) => <option key={s.id} value={s.id}>{s.course_code} {s.section_code}</option>)}
      </select>
      {sectionId && (
        <>
          <div className="flex items-end gap-2 mb-3">
            <div className="flex-1"><StudentPicker students={students} value={addId} onChange={setAddId} testid="roster-add-search" /></div>
            <Button data-testid="roster-add-btn" size="sm" disabled={!addId} onClick={add}>Add to section</Button>
          </div>
          {(roster || []).map((r) => (
            <div key={r.id} className="flex items-center justify-between gap-2 py-1.5 border-t border-slate-50 text-sm" data-testid={`roster-row-${r.id}`}>
              <span>{r.student_name} <span className={`text-[10px] px-1.5 py-0.5 rounded ${STATUS_BADGE[r.status] || 'bg-slate-100'}`}>{r.status}{r.waitlist_position ? ` #${r.waitlist_position}` : ''}</span></span>
              {['registered', 'waitlisted', 'offered'].includes(r.status) && <button data-testid={`roster-drop-${r.id}`} className="text-xs text-rose-500 underline" onClick={() => drop(r.id)}>Drop</button>}
            </div>
          ))}
          {(roster || []).length === 0 && <p className="text-sm text-slate-400">Nobody is on this section yet.</p>}
        </>
      )}
    </Card>
  );
};

const HoldsTab = ({ students }) => {
  const qc = useQueryClient();
  const { data: holds } = useQuery({ queryKey: ['reg-holds'], queryFn: () => api.get('/reg/holds').then((r) => r.data) });
  const [form, setForm] = useState({ student_id: null, hold_type: 'financial', blocks: ['registration'], reason: '', release_condition: '' });
  const place = useMutation({
    mutationFn: () => api.post('/reg/holds', form),
    onSuccess: () => { toast.success('Hold placed'); qc.invalidateQueries({ queryKey: ['reg-holds'] }); setForm({ student_id: null, hold_type: 'financial', blocks: ['registration'], reason: '', release_condition: '' }); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const release = async (id) => {
    const reason = window.prompt('Reason for releasing this hold?');
    if (!reason) return;
    try { await api.post(`/reg/holds/${id}/release`, { reason }); toast.success('Hold released'); qc.invalidateQueries({ queryKey: ['reg-holds'] }); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const toggleBlock = (b) => setForm((f) => ({ ...f, blocks: f.blocks.includes(b) ? f.blocks.filter((x) => x !== b) : [...f.blocks, b] }));
  return (
    <div className="grid lg:grid-cols-2 gap-4">
      <Card testid="holds-place">
        <p className="font-display font-bold mb-2">Place a hold</p>
        <div className="space-y-3">
          <StudentPicker students={students} value={form.student_id} onChange={(v) => setForm((f) => ({ ...f, student_id: v }))} testid="hold-student-search" />
          <select data-testid="hold-type" className="border border-slate-200 rounded-md text-sm px-2 py-2 w-full capitalize" value={form.hold_type} onChange={(e) => setForm((f) => ({ ...f, hold_type: e.target.value }))}>
            {HOLD_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <div className="flex flex-wrap gap-2">
            {HOLD_BLOCKS.map((b) => (
              <label key={b} className={`text-xs border rounded-full px-2.5 py-1 cursor-pointer ${form.blocks.includes(b) ? 'bg-slate-900 text-white border-slate-900' : 'border-slate-200'}`}>
                <input type="checkbox" className="hidden" checked={form.blocks.includes(b)} onChange={() => toggleBlock(b)} data-testid={`hold-block-${b}`} />blocks {b}
              </label>
            ))}
          </div>
          <Input data-testid="hold-reason" placeholder="Reason (shown to the student)" value={form.reason} onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))} />
          <Input data-testid="hold-release-condition" placeholder="Release condition (e.g. fees cleared)" value={form.release_condition} onChange={(e) => setForm((f) => ({ ...f, release_condition: e.target.value }))} />
          <Button data-testid="hold-place-btn" className="bg-slate-900 hover:bg-slate-800" disabled={!form.student_id || !form.reason.trim() || place.isPending} onClick={() => place.mutate()}>Place hold</Button>
        </div>
      </Card>
      <Card testid="holds-list">
        <div className="flex items-center justify-between mb-2">
          <p className="font-display font-bold">Holds</p>
          <Button data-testid="finance-sweep-btn" size="sm" variant="outline"
            onClick={async () => { try { const { data } = await api.post('/reg/holds/finance-sweep'); toast.success(`Finance sweep: ${data.placed} placed, ${data.released} released`); qc.invalidateQueries({ queryKey: ['reg-holds'] }); } catch (e) { toast.error(errMsg(e)); } }}>
            Run finance sweep now</Button>
        </div>
        <p className="text-xs text-slate-400 mb-2">Financial holds place themselves when fees are overdue past the grace period and release themselves on payment.</p>
        <div className="max-h-96 overflow-auto">
          {(holds || []).map((h) => (
            <div key={h.id} className="py-2 border-t border-slate-50 text-sm flex items-start justify-between gap-2" data-testid={`hold-row-${h.id}`}>
              <div>
                <p className="font-medium">{h.student_name} <span className={`text-[10px] px-1.5 py-0.5 rounded capitalize ${h.status === 'active' ? 'bg-rose-100 text-rose-600' : 'bg-slate-100 text-slate-400'}`}>{h.hold_type} · {h.status}</span></p>
                <p className="text-xs text-slate-500">{h.reason} · blocks {(h.blocks || []).join(', ')} · by {h.placed_by}</p>
                {h.release_condition && <p className="text-xs text-slate-400">Release when: {h.release_condition}</p>}
              </div>
              {h.status === 'active' && <button data-testid={`hold-release-${h.id}`} className="text-xs text-blue-600 underline shrink-0" onClick={() => release(h.id)}>Release</button>}
            </div>
          ))}
          {(holds || []).length === 0 && <p className="text-sm text-slate-400">No holds on any student.</p>}
        </div>
      </Card>
    </div>
  );
};

export default function RegistrationDesk() {
  const { t } = useI18n();
  const { data: students } = useQuery({ queryKey: ['students'], queryFn: () => api.get('/students').then((r) => r.data) });
  const { data: sections } = useQuery({ queryKey: ['acad-sections-all'], queryFn: () => api.get('/acad/sections').then((r) => r.data) });
  return (
    <div className="space-y-5" data-testid="registration-desk-page">
      <div>
        <h1 className="font-display text-2xl font-extrabold tracking-tight flex items-center gap-2"><ClipboardText size={26} className="text-orange-600" />{t('Registration desk')}</h1>
        <p className="text-sm text-slate-500">{t('Register students fast, run whole cohorts with a preview, manage rosters and holds.')}</p>
      </div>
      <Tabs defaultValue="register">
        <TabsList className="h-auto flex-wrap">
          {[['register', 'Register student'], ['bulk', 'Bulk registration'], ['roster', 'Section roster'], ['holds', 'Holds']].map(([v, l]) => (
            <TabsTrigger key={v} value={v} data-testid={`regdesk-tab-${v}`} className="h-auto py-2">{t(l)}</TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="register" className="mt-4"><RegisterTab students={students} sections={sections} /></TabsContent>
        <TabsContent value="bulk" className="mt-4"><BulkTab sections={sections} /></TabsContent>
        <TabsContent value="roster" className="mt-4"><RosterTab sections={sections} students={students} /></TabsContent>
        <TabsContent value="holds" className="mt-4"><HoldsTab students={students} /></TabsContent>
      </Tabs>
    </div>
  );
}
